import React, { Component, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { Alert, AlertDescription, Button, Skeleton } from '@cargomatic/ui';
import { ChevronLeft, ChevronRight, TriangleAlert } from 'lucide-react';
import { isPDFEncrypted } from 'utils/documents';

const LazyPdf = lazy(async () => {
  const { Document, Page, pdfjs } = await import(
    /* webpackChunkName: "pdf-viewer" */ 'react-pdf'
  );
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
  return {
    default: function LazyPdfRenderer({
      file,
      onLoadSuccess,
      pageNumber,
      width,
      height,
    }) {
      return (
        <Document file={file} onLoadSuccess={onLoadSuccess}>
          <Page
            pageNumber={pageNumber}
            width={width}
            height={height}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      );
    },
  };
});

class PDFViewer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      encrypted: false,
      page: 1,
      pages: 0,
    };
  }

  async componentDidMount() {
    if (this.props.file instanceof File) {
      const encrypted = await isPDFEncrypted(this.props.file);
      this.setState({ loading: false, encrypted });
    } else {
      this.setState({ loading: false });
    }
  }

  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ page: 1, pages: numPages });
  };

  handlePrevious = () => {
    this.setState((prevState) => ({ page: prevState.page - 1 }));
  };

  handleNext = () => {
    this.setState((prevState) => ({ page: prevState.page + 1 }));
  };

  renderPagination(page, pages) {
    const { t, paginationContainer } = this.props;
    const wrapperClass = paginationContainer
      ? 'flex flex-1 items-center justify-between gap-2'
      : 'flex items-center justify-between mt-2 gap-2';
    const controls = (
      <div className={wrapperClass}>
        <Button
          variant="outline"
          size="sm"
          onClick={this.handlePrevious}
          disabled={page === 1}
        >
          <ChevronLeft className="size-4 mr-1" />
          {titleize(t('PREVIOUS'))}
        </Button>
        <span className="text-sm text-muted-foreground">
          {page} / {pages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={this.handleNext}
          disabled={page === pages}
        >
          {titleize(t('NEXT'))}
          <ChevronRight className="size-4 ml-1" />
        </Button>
      </div>
    );

    if (paginationContainer) {
      return createPortal(controls, paginationContainer);
    }
    return controls;
  }

  renderPDF = () => {
    const { t } = this.props;

    if (this.state.loading) {
      return <Skeleton className="h-96 w-full" />;
    }

    if (this.state.encrypted) {
      return (
        <Alert variant="destructive">
          <TriangleAlert className="size-4" />
          <AlertDescription>{t('PDF_DOCUMENT_ENCRYPTED')}</AlertDescription>
        </Alert>
      );
    }

    const pageWidth =
      typeof this.props.width === 'number'
        ? this.props.width
        : parseInt(this.props.width, 10) || undefined;
    const pageHeight =
      typeof this.props.height === 'number'
        ? this.props.height
        : parseInt(this.props.height, 10) || undefined;

    const loadingFallback = <Skeleton className="h-96 w-full" />;

    if (this.props.displayInGridView) {
      const thumbnailHeight = pageHeight || 125;
      const thumbnailWidth = pageWidth || 100;
      return (
        <div
          className="overflow-hidden"
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: thumbnailWidth,
            height: thumbnailHeight,
          }}
        >
          <a
            href={this.props.file}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'block',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Suspense fallback={loadingFallback}>
              <LazyPdf
                file={this.props.file}
                onLoadSuccess={this.onDocumentLoadSuccess}
                pageNumber={this.state.page}
                width={thumbnailWidth}
                height={thumbnailHeight}
              />
            </Suspense>
          </a>
        </div>
      );
    }

    let pagination = null;
    if (this.state.pages && this.state.pages > 1) {
      pagination = this.renderPagination(this.state.page, this.state.pages);
    }

    return (
      <div style={this.props.pdfStyle}>
        <Suspense fallback={loadingFallback}>
          <LazyPdf
            file={this.props.file}
            onLoadSuccess={this.onDocumentLoadSuccess}
            pageNumber={this.state.page}
          />
        </Suspense>
        {pagination}
      </div>
    );
  };

  render() {
    return this.renderPDF();
  }
}

export default withTranslation()(PDFViewer);
